import { resetAuthFailureCounter } from '@/lib/auth/recovery'
import { RootState } from '@/lib/store'
import { Action, PayloadAction } from '@reduxjs/toolkit'
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'
import { HYDRATE } from 'next-redux-wrapper'
import posthog from 'posthog-js'
import Session from 'supertokens-web-js/recipe/session'

/**
 * Stores the backend request ID from the most recent API response.
 * Used for correlating frontend errors with backend logs.
 *
 * Note: This is a module-level variable that may be overwritten during concurrent requests.
 * In practice, this is acceptable for error correlation because:
 * 1. Most errors occur synchronously during or immediately after a request
 * 2. The request ID will typically be from the most recent request that triggered the error
 * 3. Having an approximate request ID is better than none for debugging
 *
 * For more precise correlation, errors should be caught and reported within the request
 * handler where the exact request ID is available in the response headers.
 */
let lastRequestId: string | undefined

/**
 * Get the most recent backend request ID from API responses.
 * Useful for correlating frontend errors with backend logs in Sentry/GCP.
 */
export function getLastRequestId(): string | undefined {
  return lastRequestId
}

export const TAG_TYPES = [
  'AdMetricsTimeseries',
  'Agent',
  'BlogPost',
  'BusinessProfile',
  'UserQuota',
  'User',
  'UserAdminStatus',
  'Workflow',
  'WorkflowMetadata',
  'Sessions',
  'ScheduledTasks',
  'ScheduledTaskResults',
  'VanityMappings',
  'OnboardingData',
  'Tag',
  'Execution',
  'UserPrompt',
  'IntegrationCredentials',
  'StatusBanner',
] as const
export type TagType = (typeof TAG_TYPES)[number]

function isHydrateAction(action: Action): action is PayloadAction<RootState, string, never, never> {
  return action.type === HYDRATE
}

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI,
  credentials: 'include',
  prepareHeaders: (headers) => {
    // Inject PostHog correlation IDs for cross-system observability
    try {
      if (posthog.__loaded) {
        const sessionId = posthog.get_session_id()
        const distinctId = posthog.get_distinct_id()

        if (sessionId) {
          headers.set('X-PostHog-Session-Id', sessionId)
        }
        if (distinctId) {
          headers.set('X-PostHog-Distinct-Id', distinctId)
        }
      }
    } catch (error) {
      // Silently fail - don't block requests if PostHog isn't available
      console.warn('[API] Failed to add PostHog headers:', error)
    }

    return headers
  },
  fetchFn: async (...args) => {
    const response = await fetch(...args)

    // Capture backend request ID from response headers for error correlation
    const requestId = response.headers.get('X-Request-Id')
    if (requestId) {
      lastRequestId = requestId
    }

    return response
  },
})

const baseQueryWithReauthAndRetry = retry(
  async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions)

    // If the request fails with a 401, try recovery steps.
    if (
      result.error?.status === 401 ||
      ('error' in (result.error ?? {}) && (result.error as { error: string }).error.includes('401'))
    ) {
      // Check if a SuperTokens session exists
      const hasSession = await Session.doesSessionExist()
      if (!hasSession) {
        // No session exists - this is an expected 401 when trying to create an anonymous session.
        // Don't record this as a failure. The middleware will handle recording if needed.
        // Try to create an anonymous session by fetching the user
        const userResponse = await baseQuery(
          { url: 'v1/users/me', method: 'GET' },
          api,
          extraOptions
        )
        if (!userResponse.error) {
          resetAuthFailureCounter()
          // Retry the original request
          return await baseQuery(args, api, extraOptions)
        }
        // If fetching the user fails, fall through to fail
        // The middleware will record this as a failure if it's unexpected
      }
      retry.fail(
        {
          status: 401,
          data: {
            code: '401',
            message: 'Unauthorized',
          },
        },
        result.meta
      )
    }

    return result
  },
  { maxRetries: 3 }
)

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauthAndRetry,
  tagTypes: TAG_TYPES,
  extractRehydrationInfo(action, { reducerPath }): any {
    if (isHydrateAction(action)) {
      return action.payload[reducerPath]
    }
  },
  keepUnusedDataFor: 300,
  refetchOnMountOrArgChange: 60,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  endpoints: () => ({}),
})
